/**
 * The invitation this browser is in the middle of taking. An invite link is `/invite#<token>`: the token rides in the
 * fragment, which browsers never send to a server, and never in a path or query string, which servers log. The app
 * reads it on arrival (takeInviteFragment, before anything else starts), keeps it here and takes it out of the address
 * bar. Signing in then leaves with a plain `return_to=/invite`, and coming back (in the same tab from Google or
 * Strava, or in a new one from an emailed link) finds it again. localStorage, not sessionStorage, for that new tab;
 * two hours, so a link left half-taken doesn't linger. Where storage is refused (some private windows) this tab still
 * has it in memory.
 *
 * Accepting it, learning it is unknown, learning it is gone for this account (a signed-in check), or signing out
 * (AuthContext's logout) clears it. A link gone while signed out stays, as it may still be the visitor's (a host
 * invitation they took, a guest link to a stage they are on), and so does a refusal that holds for one account only
 * (already a host, blocked from the stage's chat). The first account signed in with it claims it (bindPendingInvite):
 * on a shared device, someone signing in after a session that ended without signing out finds nothing, since another
 * account claimed it. A link opened signed out and left before anyone signed in is the one case left; it still needs
 * that person to open /invite themselves within the two hours, and a tap to accept.
 */
export const PENDING_INVITE_KEY = 'sb_pending_invite'
export const PENDING_INVITE_TTL_MS = 2 * 60 * 60 * 1000

/** The API's token: a SHA-256 HMAC in hex (`Invitation::TOKEN_FORMAT`). */
const TOKEN_FORMAT = /^[0-9a-f]{64}$/i

/** The token in a URL fragment (`#<token>`), or null for one that is no token, such as a skip link's `#invite-main`. */
export function inviteTokenFromHash(hash: string): string | null {
  const value = hash.replace(/^#/, '')
  return TOKEN_FORMAT.test(value) ? value : null
}

/** `userId`: the account that claimed it (bindPendingInvite); none until someone signed in has seen it. */
type Entry = { token: string; expiresAt: number; userId?: string }

/** This tab's copy, read only when storage refused it (signing in by email then needs the link opened again). */
let inMemory: Entry | null = null
/** The last save didn't reach storage (full, or refused), so storage holds an older entry or none. */
let writeRefused = false

/** Remembers `token` for two hours, unclaimed: a new link is whoever opened it's. */
export function savePendingInvite(token: string, now = Date.now()) {
  write({ token, expiresAt: now + PENDING_INVITE_TTL_MS })
}

/** The remembered token, or null. An expired or unreadable entry is removed. */
export function readPendingInvite(now = Date.now()): string | null {
  return readEntry(now)?.token ?? null
}

/**
 * Claims the pending invite for the signed-in account `userId`, if no one has: true when it is this account's, or there
 * is none. False when another account claimed it, which forgets it, so the next person to sign in on a shared device
 * can't take someone else's invitation.
 */
export function bindPendingInvite(userId: string, now = Date.now()): boolean {
  const entry = readEntry(now)
  if (!entry || entry.userId === userId) return true
  if (entry.userId) {
    clearPendingInvite()
    return false
  }
  write({ ...entry, userId })
  return true
}

function write(entry: Entry) {
  inMemory = entry
  try {
    window.localStorage.setItem(PENDING_INVITE_KEY, JSON.stringify(entry))
    writeRefused = false
  } catch {
    writeRefused = true
  }
}

function readEntry(now: number): Entry | null {
  let entry: Entry | null
  if (writeRefused) entry = inMemory
  else {
    try {
      const stored = window.localStorage.getItem(PENDING_INVITE_KEY)
      if (stored === null) return null
      entry = parse(stored)
    } catch {
      entry = inMemory
    }
  }
  if (entry && entry.expiresAt > now) return entry
  clearPendingInvite()
  return null
}

export function clearPendingInvite() {
  inMemory = null
  writeRefused = false
  try {
    window.localStorage.removeItem(PENDING_INVITE_KEY)
  } catch {
    // Nothing was stored.
  }
}

/** The token for the invite page: the fragment's, remembered for after signing in, else the one remembered earlier. */
export function resolveInviteToken(hash: string, now = Date.now()): string | null {
  const fromLink = inviteTokenFromHash(hash)
  if (!fromLink) return readPendingInvite(now)
  savePendingInvite(fromLink, now)
  return fromLink
}

function parse(stored: string): Entry | null {
  try {
    const value: unknown = JSON.parse(stored)
    if (typeof value !== 'object' || value === null) return null
    const { token, expiresAt, userId } = value as Record<string, unknown>
    if (typeof token !== 'string' || !TOKEN_FORMAT.test(token) || typeof expiresAt !== 'number') return null
    if (userId !== undefined && typeof userId !== 'string') return null
    return userId === undefined ? { token, expiresAt } : { token, expiresAt, userId }
  } catch {
    return null
  }
}
